import { Fragment, useEffect } from 'react'
import { range } from 'lodash'

import { useReselector } from '@/utils/sharedHooks'
import { selectSortedTaxReturnDocumentsForYear } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import {
  FETCH_USER_DOCUMENTS_KEY,
  fetchUserDocuments,
  UserDocument,
} from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { PageHeader } from '@/components/PageHeader'
import AnnualTaxFaq from '@/features/taxes/annual-taxes/overview/AnnualTaxFaq'

import { Text, Link, Accordion, Separator, Loader } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { useAppDispatch } from '@/utils/typeHelpers'
import { selectIsFetchingForKeys } from '@/reducers/fetch'
import {
  FETCH_TAX_USER_DOCUMENTS_KEY,
  fetchTaxUserDocumentsIfNeeded,
} from '../annual-taxes/taxUserDocuments.slice'
import {
  FETCH_DOCUMENT_CATEGORIES_KEY,
  fetchUserDocumentCategoriesIfNeeded,
} from '@/v2-features/admin/user-document-categories/userDocumentCategories.slice'
import { PostTaxYearFiling, usePostTaxYearStatus } from './PostTaxYearFiling'

const AnnualTaxFilingFormTaxDocument = ({
  document,
}: {
  document: UserDocument
}) =>
  document.signedUrl ? (
    <div className="w-full md:w-1/2">
      <Link href={document.signedUrl} newPage variant="secondary">
        {document.fileDescription}
      </Link>
    </div>
  ) : null

const ReturnSection = ({
  title,
  documents,
}: {
  title: string
  documents: UserDocument[]
}) =>
  documents.length ? (
    <>
      <div className="w-full">
        <Text variant={TypographyStyles.EYEBROW}>{title}</Text>
      </div>
      {documents.map((doc) => (
        <AnnualTaxFilingFormTaxDocument key={doc.id} document={doc} />
      ))}
    </>
  ) : null

const ReturnYear = ({ year }: { year: number }) => {
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const { status: postTaxYearStatus, refresh: refreshPostTaxYearStatus } =
    usePostTaxYearStatus(year.toString())

  const businessTaxReturnsForYear = useReselector(
    selectSortedTaxReturnDocumentsForYear,
    year.toString()
  )?.business

  const personalTaxReturnsForYear = useReselector(
    selectSortedTaxReturnDocumentsForYear,
    year.toString()
  )?.personal

  const otherTaxReturnsForYear =
    useReselector(selectSortedTaxReturnDocumentsForYear, year.toString())
      ?.other || []

  if (
    !businessTaxReturnsForYear.length &&
    !personalTaxReturnsForYear.length &&
    !otherTaxReturnsForYear.length
  ) {
    return (
      <>
        <PostTaxYearFiling
          year={year.toString()}
          status={postTaxYearStatus}
          onUpdated={refreshPostTaxYearStatus}
        />
        {!postTaxYearStatus && (
          <div className="w-full">
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              {currentTaxYear === year.toString() ? (
                <>
                  When you are finished filing taxes with us, a copy of your
                  returns will show here.
                  <br />
                  <br />
                  If you&apos;ve already completed your taxes, but you
                  don&apos;t see your returns here, please upload them in the
                  Tax Documents section of the Documents page.
                </>
              ) : (
                <>
                  It looks like we don’t have your {year} tax returns. You can
                  upload your return via the Tax Documents section of the
                  Documents page.
                </>
              )}{' '}
              <Link href="/practice/documents?tab=taxes" variant="primary">
                Go to Documents
              </Link>
            </Text>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <PostTaxYearFiling
        year={year.toString()}
        status={postTaxYearStatus}
        onUpdated={refreshPostTaxYearStatus}
      />
      <ReturnSection title="business" documents={businessTaxReturnsForYear} />
      <ReturnSection title="personal" documents={personalTaxReturnsForYear} />
      <ReturnSection title="other" documents={otherTaxReturnsForYear} />
    </>
  )
}

export const TaxReturnArchive = () => {
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchUserDocuments())
    dispatch(fetchTaxUserDocumentsIfNeeded())
    dispatch(fetchUserDocumentCategoriesIfNeeded())
  }, [dispatch])

  const isFetching = useReselector(selectIsFetchingForKeys, [
    FETCH_USER_DOCUMENTS_KEY,
    FETCH_TAX_USER_DOCUMENTS_KEY,
    FETCH_DOCUMENT_CATEGORIES_KEY,
  ])

  if (isFetching) {
    return <Loader />
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader header="Tax Return Archive" />
      <div className="flex flex-col md:flex-row gap-6">
        <div className="w-full md:w-2/3 space-y-6">
          <Text variant={TypographyStyles.BODY_SM}>
            Not seeing one of your returns? You can upload them in the Tax
            Documents section of the Documents page.{' '}
            <Link href="/practice/documents?tab=taxes">Go to Documents</Link>
          </Text>

          {range(Number(currentTaxYear), 2020, -1).map((year) => (
            <Fragment key={year}>
              <Accordion
                type="multiple"
                variant="header"
                items={[
                  {
                    title: `${year} Tax Returns`,
                    content: <ReturnYear year={year} />,
                  },
                ]}
              />
              <Separator />
            </Fragment>
          ))}
        </div>
        <div className="w-full md:w-1/3">
          <AnnualTaxFaq />
        </div>
      </div>
    </div>
  )
}
